import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import {
  DASHBOARD_DIMENSIONS,
  DashboardSchema,
  type DashboardDimension,
  type DashboardMeasureDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { ApiError, getData } from '../../shared/api/client';
import { ExportButton } from '../../shared/export/ExportButton';
import { useSession } from '../../shared/session/session';
import { LIVE_QUERY, LiveStatus } from '../../shared/ui/live';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { businessToday, DASHBOARD_LINKS, presetPeriod, type PeriodPreset } from './dashboards';
import { DimensionFilters } from './dimensions';
import { SearchFilterBar } from '../../shared/ui/filters';

const PRESETS: readonly [PeriodPreset, string][] = [
  ['month', 'This month'],
  ['quarter', 'This quarter'],
  ['year', 'This year'],
  ['custom', 'Custom'],
];

/**
 * One Release 1 dashboard (`/dashboards/:code`; FRD-RPT-001 to RPT-032, SDD §22.7). The period is
 * kept in the URL (FRD-RPT-010): a preset to date, or a custom from/to; so are the dimension
 * filters (department, cost centre, project, location), which the API applies only to measures
 * whose source lists support them — any other measure says it is not filterable by that
 * dimension rather than showing an unfiltered figure. Every measure is a card with
 * its value, unit and basis (FRD-RPT-011), a breakdown where the module gives one, and a link to
 * the filtered list behind it (FRD-RPT-007). A measure that cannot be presented says why — its
 * capability is not delivered, or the session may not see its records — and never shows a zero
 * in its place (FRD-RPT-013). Figures are the API's; nothing is added up here.
 */
export function ReportingDashboardPage() {
  const { code = '' } = useParams();
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const dashboard = DASHBOARD_LINKS.find((d) => d.code === code);

  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');
  const presetParam = params.get('period');
  const preset: PeriodPreset = PRESETS.some(([p]) => p === presetParam)
    ? (presetParam as PeriodPreset)
    : 'month';
  const range =
    preset === 'custom'
      ? { from: params.get('from') ?? '', to: params.get('to') ?? '' }
      : presetPeriod(preset, today);
  const complete = range.from !== '' && range.to !== '' && range.from <= range.to;
  const dimensions: Partial<Record<DashboardDimension, string>> = {};
  const queryParams = new URLSearchParams({ from: range.from, to: range.to });
  for (const key of DASHBOARD_DIMENSIONS) {
    const value = params.get(key) ?? '';
    if (value !== '') {
      dimensions[key] = value;
      queryParams.set(key, value);
    }
  }
  const query = queryParams.toString();

  const data = useQuery({
    queryKey: ['reporting', 'dashboard', code, query],
    enabled: dashboard !== undefined && complete,
    queryFn: () => getData(`/api/v1/reporting/dashboards/${code}?${query}`, DashboardSchema),
    ...LIVE_QUERY,
  });

  if (dashboard === undefined) {
    return (
      <Stack spacing={3}>
        <PageHeader title="Dashboard" />
        <Section>
          <EmptyState title="No such dashboard" detail="Choose a dashboard from the menu." />
        </Section>
      </Stack>
    );
  }

  const setPeriod = (next: Record<string, string>) => {
    const updated = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === '') updated.delete(key);
      else updated.set(key, value);
    }
    setParams(updated);
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title={dashboard.title}
        description="Figures from the modules that own them, limited to what you may see."
        actions={
          complete ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <LiveStatus
                updatedAt={data.dataUpdatedAt}
                refreshing={data.isFetching}
                onRefresh={() => {
                  void data.refetch();
                }}
              />
              <ExportButton
                recordType={`reporting.${dashboard.code}_dashboard`}
                href={`/api/v1/reporting/dashboards/${dashboard.code}/export?${query}`}
              />
            </Stack>
          ) : undefined
        }
      />
      <SearchFilterBar aria-label="Dashboard period and filters">
        <TextField
          select
          size="small"
          label="Period"
          value={preset}
          onChange={(e) => {
            const next = e.target.value as PeriodPreset;
            if (next === 'custom') {
              setPeriod({ period: 'custom', from: range.from, to: range.to });
            } else {
              setPeriod({ period: next, from: '', to: '' });
            }
          }}
          sx={{ minWidth: 180 }}
        >
          {PRESETS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label="From"
          value={range.from}
          disabled={preset !== 'custom'}
          onChange={(e) => {
            setPeriod({ period: 'custom', from: e.target.value, to: range.to });
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={range.to}
          disabled={preset !== 'custom'}
          onChange={(e) => {
            setPeriod({ period: 'custom', from: range.from, to: e.target.value });
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {/* The dimension filters lay themselves out; they take the full width under the period. */}
        <Box role="group" aria-label="Dashboard filters" sx={{ gridColumn: '1 / -1' }}>
          <DimensionFilters
            values={dimensions}
            onChange={(key, value) => {
              setPeriod({ [key]: value });
            }}
          />
        </Box>
      </SearchFilterBar>
      {!complete && <ErrorState message="Choose a period whose start is on or before its end." />}
      {complete && data.isPending && <LoadingState label="Loading the dashboard" />}
      {complete && data.isError && (
        <ErrorState
          message={
            data.error instanceof ApiError && data.error.status === 403
              ? 'You do not have permission to open this dashboard.'
              : data.error.message
          }
        />
      )}
      {data.data !== undefined && data.data.measures.length === 0 && (
        <Section>
          <EmptyState title="This dashboard presents no measures" />
        </Section>
      )}
      {data.data !== undefined && data.data.measures.length > 0 && (
        <Grid container spacing={2.5}>
          {data.data.measures.map((measure) => (
            <Grid key={measure.code} size={{ xs: 12, sm: 6, lg: 4 }}>
              <MeasureCard measure={measure} />
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}

function formatValue(measure: DashboardMeasureDto, value: string): string {
  if (measure.unit === 'AED') return `AED ${value}`;
  if (measure.unit === 'days') return `${value} days`;
  if (measure.unit === 'percent') return `${value}%`;
  return value;
}

/** A breakdown line: a rate's lines are the counts it is computed from, never percentages. */
function formatLine(measure: DashboardMeasureDto, value: string): string {
  return measure.unit === 'percent' ? value : formatValue(measure, value);
}

/** The chip on a measure without a value: why, in two or three words. */
function unavailableChip(measure: DashboardMeasureDto): string {
  switch (measure.unavailable) {
    case 'not_filterable':
      return 'Not filterable';
    case 'failed':
      return 'Could not be calculated';
    default:
      return 'Not available';
  }
}

function MeasureCard({ measure }: { measure: DashboardMeasureDto }) {
  const headingId = `measure-${measure.code}`;
  return (
    <Paper
      component="section"
      aria-labelledby={headingId}
      sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography id={headingId} variant="subtitle1" component="h2">
        {measure.label}
      </Typography>
      {measure.available && measure.value !== undefined ? (
        <Typography
          sx={{ fontSize: '1.75rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatValue(measure, measure.value)}
        </Typography>
      ) : (
        <Box>
          <Chip size="small" label={unavailableChip(measure)} variant="outlined" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {measure.reason}
          </Typography>
        </Box>
      )}
      {measure.available && measure.scopeLimited === true && (
        <Typography variant="body2" color="text.secondary">
          Limited to your scope: it counts only the records your access reaches.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {measure.basis}
      </Typography>
      {measure.available &&
        measure.breakdown !== undefined &&
        measure.breakdown.length > 0 &&
        (measure.chart === 'pie' ? <Pie measure={measure} /> : <Breakdown measure={measure} />)}
      {measure.available && measure.drillPath !== undefined && (
        <Box sx={{ mt: 'auto' }}>
          <Link component={RouterLink} to={measure.drillPath} sx={{ fontWeight: 600 }}>
            View records
            <Box component="span" sx={visuallyHidden}>
              {` for ${measure.label}`}
            </Box>
          </Link>
        </Box>
      )}
    </Paper>
  );
}

// Pixel strings: MUI reads the numbers 1 as 100%, which made the hidden text as wide as the card.
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** A line's number; a hidden small count ("<3", CLR-47) draws no bar and no slice. */
function amountOf(value: string): number {
  const n = Math.abs(Number(value));
  return Number.isFinite(n) ? n : 0;
}

/** The slice colours, in order; each pairs with its legend entry, so colour is not the only cue. */
const PIE_COLOURS = ['#1f77b4', '#f2b705', '#d64545', '#2ca58d', '#7e57c2', '#8d6e63', '#78909c'];

/**
 * A pie of shares (CLR-47): the slices are drawn from the counts, and the legend beside it names
 * every line with its count and share, so the figures are readable without the picture.
 */
function Pie({ measure }: { measure: DashboardMeasureDto }) {
  const lines = measure.breakdown ?? [];
  const total = lines.reduce((sum, line) => sum + amountOf(line.value), 0);
  const shares = lines.map((line) => (total === 0 ? 0 : amountOf(line.value) / total));
  const slices = lines.map((line, index) => {
    const before = shares.slice(0, index).reduce((sum, share) => sum + share, 0);
    const share = shares[index] ?? 0;
    return {
      line,
      share,
      start: -Math.PI / 2 + before * 2 * Math.PI,
      end: -Math.PI / 2 + (before + share) * 2 * Math.PI,
      colour: PIE_COLOURS[index % PIE_COLOURS.length],
    };
  });
  const point = (a: number) => `${String(50 + 45 * Math.cos(a))} ${String(50 + 45 * Math.sin(a))}`;
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      <Box component="svg" viewBox="0 0 100 100" aria-hidden sx={{ width: 112, flexShrink: 0 }}>
        {slices.map((slice) =>
          slice.share >= 0.9999 ? (
            <circle key={slice.line.label} cx="50" cy="50" r="45" fill={slice.colour} />
          ) : slice.share > 0 ? (
            <path
              key={slice.line.label}
              d={`M50 50 L${point(slice.start)} A45 45 0 ${slice.share > 0.5 ? 1 : 0} 1 ${point(slice.end)} Z`}
              fill={slice.colour}
            />
          ) : null,
        )}
      </Box>
      <Stack component="ul" spacing={0.75} sx={{ listStyle: 'none', m: 0, p: 0, minWidth: 0 }}>
        {slices.map((slice) => (
          <Stack
            component="li"
            key={slice.line.label}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center' }}
          >
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: slice.colour,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {slice.line.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
              {formatLine(measure, slice.line.value)}
              {total > 0 && ` (${String(Math.round(slice.share * 100))}%)`}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

/** A labelled bar list: each line's value, with a bar relative to the largest line. */
function Breakdown({ measure }: { measure: DashboardMeasureDto }) {
  const lines = measure.breakdown ?? [];
  const largest = Math.max(...lines.map((line) => amountOf(line.value)), 0);
  return (
    <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {lines.map((line) => {
        const share = largest === 0 ? 0 : (amountOf(line.value) / largest) * 100;
        return (
          <Box component="li" key={`${line.label}-${line.drillPath ?? ''}`}>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
              {line.drillPath === null ? (
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {line.label}
                </Typography>
              ) : (
                <Link
                  component={RouterLink}
                  to={line.drillPath}
                  variant="body2"
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {line.label}
                </Link>
              )}
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatLine(measure, line.value)}
              </Typography>
            </Stack>
            <Box
              aria-hidden
              sx={{
                mt: 0.5,
                height: 6,
                borderRadius: 3,
                bgcolor: 'action.hover',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ width: `${String(share)}%`, height: '100%', bgcolor: 'primary.main' }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
